/**
 * Back office → Academy → « Mock orals with a trainer » (D-142): the planning of the sessions,
 * a session sheet (official outline of the exam's oral, open the room, mark each task on the
 * exam's grid, write the report, record an absence, record a refund), the trainers' windows and
 * the month's statistics. D-120: a trainer sees their own sessions in their language(s), without
 * the payments; the owner and the manager see everything.
 */

import {
  type OralBookingAdmin,
  useAcademyOralAvailabilityCreate,
  useAcademyOralAvailabilityDestroy,
  useAcademyOralAvailabilityList,
  useAcademyOralSessionsAbsentCreate,
  useAcademyOralSessionsGradeCreate,
  useAcademyOralSessionsList,
  useAcademyOralSessionsRefundCreate,
  useAcademyOralSessionsStartCreate,
  useAcademyOralSessionsStatsRetrieve,
  useCrmMembersList,
} from "@agencyos/api-client";
import { clampMark, taskDuration, formatXaf, localText, oralMark, zonedWhen } from "@agencyos/core";
import { Badge, type BadgeTone, Button, Checkbox, GlassCard, GlassSheet, SelectField, Skeleton, TextAreaField, TextField } from "@agencyos/ui-web";
import { Link2, Play, Plus } from "lucide-react";
import { type FormEvent, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAdminExam } from "./examContext";
import { ErrorList, Pager, isObject, useAcademyRoles, useRefreshAcademy } from "./shared";

const EXAMS = ["tcf_canada", "tef_canada", "ielts_general", "goethe"] as const;
const EXAM_NAMES: Record<string, string> = {
  tcf_canada: "TCF Canada",
  tef_canada: "TEF Canada",
  ielts_general: "IELTS",
  goethe: "Goethe",
};
const TONES: Record<string, BadgeTone> = {
  pending: "warning",
  confirmed: "offer",
  done: "success",
  cancelled: "default",
  abandoned: "default",
  learner_absent: "danger",
  trainer_absent: "warning",
};

type Texts = Record<string, string>;
interface Criterion {
  key: string;
  label: Texts;
  max: number;
  step: number | null;
  points: number[] | null;
}
interface GridRow {
  production: string;
  task: number;
  theme: string;
  prompt: string;
  criteria: Criterion[];
  marks: { key: string; points?: number; band?: number }[];
}

function asGrid(value: unknown): GridRow[] {
  return Array.isArray(value) ? (value as GridRow[]) : [];
}

function StatsCard({ money }: { money: boolean }) {
  const { t } = useTranslation();
  const stats = useAcademyOralSessionsStatsRetrieve({}, { query: { refetchInterval: 60_000 } });
  const data = stats.data?.status === 200 && isObject(stats.data.data) ? (stats.data.data as Record<string, unknown>) : undefined;
  if (!data) return <Skeleton style={{ height: 80 }} />;
  const n = (key: string) => (typeof data[key] === "number" ? (data[key] as number) : 0);
  const cards: [string, string][] = [
    [t("coaching.admin.stats.sessions"), String(n("sessions"))],
    [t("coaching.admin.stats.upcoming"), String(n("upcoming"))],
    [t("coaching.admin.stats.reportsLate"), String(n("reports_late"))],
    [t("coaching.admin.stats.creditsOpen"), String(n("credits_open"))],
    [t("coaching.admin.stats.refundsDue"), String(n("refunds_due"))],
  ];
  if (money && "revenue_xaf" in data) {
    cards.push(
      [t("coaching.admin.stats.revenue"), formatXaf(n("revenue_xaf"))],
      [t("coaching.admin.stats.testRevenue"), formatXaf(n("test_revenue_xaf"))],
      [t("coaching.admin.stats.refunded"), formatXaf(n("refunded_xaf"))],
    );
  }
  return (
    <GlassCard as="section" className="stack" aria-labelledby="oral-stats-title">
      <h3 id="oral-stats-title">{t("coaching.admin.stats.title")}</h3>
      <dl className="kpi-grid">
        {cards.map(([label, value]) => (
          <div key={label} className="kpi">
            <dt className="muted">{label}</dt>
            <dd className="ao-num">{value}</dd>
          </div>
        ))}
      </dl>
    </GlassCard>
  );
}

function GradeForm({ booking, onDone }: { booking: OralBookingAdmin; onDone: (b: OralBookingAdmin) => void }) {
  const { t, i18n } = useTranslation();
  const grid = asGrid(booking.grid);
  const [marks, setMarks] = useState<Record<string, Record<string, number>>>(() =>
    Object.fromEntries(grid.map((g) => [g.production, Object.fromEntries(g.marks.map((m) => [m.key, Number(m.points ?? m.band ?? 0)]))])),
  );
  const [report, setReport] = useState(booking.report ?? "");
  const grade = useAcademyOralSessionsGradeCreate();
  const set = (production: string, key: string, value: number) => setMarks((m) => ({ ...m, [production]: { ...(m[production] ?? {}), [key]: value } }));
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    grade.mutate(
      {
        id: booking.id,
        data: {
          report: report.trim(),
          marks: grid.map((g) => ({
            production: g.production,
            criteria: g.criteria.map((c) => ({ key: c.key, points: clampMark(marks[g.production]?.[c.key] ?? 0, c.max, c.step, c.points) })),
          })),
        },
      },
      { onSuccess: (r) => r.status === 200 && onDone(r.data) },
    );
  };
  return (
    <form className="stack" onSubmit={onSubmit} noValidate aria-label={t("coaching.admin.grid")}>
      <h3>{t("coaching.admin.grid")}</h3>
      {grid.map((g) => (
        <fieldset key={g.production} className="plain-fieldset stack">
          <legend>
            <strong>{t("coaching.report.task", { n: g.task })}</strong> {g.theme ? `· ${g.theme}` : ""}
          </legend>
          <p className="muted academy-small" lang="fr">
            {g.prompt}
          </p>
          <div className="form-grid">
            {g.criteria.map((c) =>
              c.points && c.points.length ? (
                <SelectField
                  key={c.key}
                  label={localText(c.label, i18n.language) || c.key}
                  value={String(marks[g.production]?.[c.key] ?? c.points[c.points.length - 1] ?? 0)}
                  options={c.points.map((p) => ({ value: String(p), label: String(p) }))}
                  onChange={(e) => set(g.production, c.key, Number(e.target.value))}
                />
              ) : (
                <TextField
                  key={c.key}
                  type="number"
                  min={0}
                  max={c.max}
                  step={c.step ?? 1}
                  label={`${localText(c.label, i18n.language) || c.key} — ${t("coaching.admin.points", { max: c.max })}`}
                  value={String(marks[g.production]?.[c.key] ?? "")}
                  onChange={(e) => set(g.production, c.key, Number(e.target.value))}
                />
              ),
            )}
          </div>
        </fieldset>
      ))}
      <TextAreaField label={t("coaching.admin.report")} hint={t("coaching.admin.reportHint")} rows={6} value={report} onChange={(e) => setReport(e.target.value)} />
      <ErrorList error={grade.error} />
      <div className="row">
        <Button type="submit" disabled={grade.isPending || !report.trim()}>
          {grade.isPending ? t("common.loading") : t("coaching.admin.save")}
        </Button>
        {grade.isSuccess || booking.status === "done" ? <span role="status">{t("coaching.admin.saved")}</span> : null}
      </div>
    </form>
  );
}

function SessionSheet({ booking, onClose, admin }: { booking: OralBookingAdmin; onClose: () => void; admin: boolean }) {
  const { t, i18n } = useTranslation();
  const refresh = useRefreshAcademy();
  const [current, setCurrent] = useState(booking);
  const start = useAcademyOralSessionsStartCreate({ mutation: { onSuccess: refresh } });
  const absent = useAcademyOralSessionsAbsentCreate({ mutation: { onSuccess: refresh } });
  const refund = useAcademyOralSessionsRefundCreate({ mutation: { onSuccess: refresh } });
  const [amount, setAmount] = useState(String(current.amount_xaf ?? ""));
  const [note, setNote] = useState("");
  const when = zonedWhen(current.starts_at, "Africa/Douala", i18n.language);
  const outline = (isObject(current.outline) ? current.outline : {}) as { tasks?: { number: number; label: Texts; seconds: number; prep_seconds: number }[]; official_seconds?: number };
  const update = (r: { status: number; data: OralBookingAdmin }) => r.status === 200 && setCurrent(r.data);
  const mark = oralMark(isObject(current.result) ? (current.result as Record<string, unknown>) : null);
  return (
    <GlassSheet open title={`${EXAM_NAMES[current.exam] ?? current.exam}${current.exam_level ? ` ${current.exam_level}` : ""} · ${current.learner_name || current.learner_email}`} onClose={onClose}>
      <div className="stack">
        <div className="row">
          <Badge tone={TONES[current.status] ?? "default"}>{t(`coaching.status.${current.status}`)}</Badge>
          {current.settlement ? <Badge>{t(`coaching.settlement.${current.settlement}`)}</Badge> : null}
          {current.is_test ? <Badge tone="warning">TEST</Badge> : null}
        </div>
        <p>
          {t("coaching.when", when)} (Africa/Douala) · {t("coaching.trainer", { name: current.trainer_full_name || current.trainer_name })}
        </p>
        {current.staff_join_url && current.status === "confirmed" ? (
          <p>
            <a href={current.staff_join_url} target="_blank" rel="noopener noreferrer" className="link">
              <Link2 aria-hidden size={16} /> {t("coaching.admin.joinRoom")}
            </a>
          </p>
        ) : null}
        <section className="stack" aria-label={t("coaching.outline.title")}>
          <h3>{t("coaching.outline.title")}</h3>
          <ol>
            {(outline.tasks ?? []).map((task) => (
              <li key={task.number}>
                <strong>{t("coaching.outline.task", { n: task.number })}</strong> · {localText(task.label, i18n.language)} —{" "}
                {t("coaching.outline.speak", { d: taskDuration(task.seconds, i18n.language) })},{" "}
                {task.prep_seconds ? t("coaching.outline.prep", { d: taskDuration(task.prep_seconds, i18n.language) }) : t("coaching.outline.noPrep")}
              </li>
            ))}
          </ol>
        </section>
        {current.status === "confirmed" && !asGrid(current.grid).length ? (
          <div className="stack">
            <p className="muted academy-small">{t("coaching.admin.openHint")}</p>
            <div className="row">
              <Button icon={<Play aria-hidden size={16} />} disabled={start.isPending} onClick={() => start.mutate({ id: current.id }, { onSuccess: update })}>
                {t("coaching.admin.open")}
              </Button>
            </div>
          </div>
        ) : null}
        {(current.status === "confirmed" || current.status === "done") && asGrid(current.grid).length ? (
          <GradeForm key={current.id + current.status} booking={current} onDone={setCurrent} />
        ) : null}
        {mark ? <p className="ao-num">{t("coaching.report.mark", { mark })}</p> : null}
        {current.status === "confirmed" ? (
          <div className="row">
            <Button variant="secondary" disabled={absent.isPending} onClick={() => absent.mutate({ id: current.id, data: { who: "learner" } }, { onSuccess: update })}>
              {t("coaching.admin.absentLearner")}
            </Button>
            {admin ? (
              <Button variant="ghost" disabled={absent.isPending} onClick={() => absent.mutate({ id: current.id, data: { who: "trainer" } }, { onSuccess: update })}>
                {t("coaching.admin.absentTrainer")}
              </Button>
            ) : null}
          </div>
        ) : null}
        {admin && (current.settlement === "refund_due" || current.settlement === "credit") && (current.amount_xaf ?? 0) > 0 ? (
          <form
            className="stack"
            onSubmit={(event) => {
              event.preventDefault();
              refund.mutate({ id: current.id, data: { amount_xaf: Number(amount) || 0, note: note.trim() } }, { onSuccess: update });
            }}
          >
            <div className="form-grid">
              <TextField type="number" min={1} label={t("coaching.admin.refundAmount")} value={amount} onChange={(e) => setAmount(e.target.value)} />
              <TextField label={t("coaching.admin.refundNote")} value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
            <div className="row">
              <Button type="submit" variant="secondary" disabled={refund.isPending || !note.trim()}>
                {t("coaching.admin.refund")}
              </Button>
              {refund.isSuccess ? <span role="status">{t("coaching.admin.refundDone")}</span> : null}
            </div>
          </form>
        ) : null}
        <ErrorList error={start.error ?? absent.error ?? refund.error} />
      </div>
    </GlassSheet>
  );
}

function AvailabilityCard({ manager }: { manager: boolean }) {
  const { t } = useTranslation();
  const list = useAcademyOralAvailabilityList({});
  const rows = list.data?.status === 200 ? list.data.data.results : [];
  const members = useCrmMembersList({ query: { enabled: manager, retry: false } });
  const team = members.data?.status === 200 ? (Array.isArray(members.data.data) ? members.data.data : []) : [];
  const create = useAcademyOralAvailabilityCreate({ mutation: { onSuccess: () => void list.refetch() } });
  const remove = useAcademyOralAvailabilityDestroy({ mutation: { onSuccess: () => void list.refetch() } });
  const [form, setForm] = useState({ trainer: "", weekday: "0", date: "", starts: "09:00", ends: "12:00", timezone: "Africa/Douala" });
  const [exams, setExams] = useState<string[]>([]);
  const set = (key: keyof typeof form, value: string) => setForm((f) => ({ ...f, [key]: value }));
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    create.mutate({
      data: {
        ...(form.trainer ? { trainer: form.trainer } : {}),
        ...(form.date ? { date: form.date, weekday: null } : { weekday: Number(form.weekday), date: null }),
        starts: form.starts,
        ends: form.ends,
        timezone: form.timezone,
        exams: exams as never,
      },
    });
  };
  return (
    <GlassCard as="section" className="stack" aria-labelledby="oral-availability-title">
      <h3 id="oral-availability-title">{t("coaching.admin.availability.title")}</h3>
      <p className="muted">{t("coaching.admin.availability.hint", { minutes: 40 })}</p>
      {rows.length ? (
        <ul className="plain-list stack">
          {rows.map((row) => (
            <li key={row.id} className="row between">
              <span>
                <strong>{row.trainer_name}</strong> ·{" "}
                {row.date ? row.date : t(`coaching.admin.availability.weekdays.${String(row.weekday ?? 0)}`)} · {row.starts.slice(0, 5)}–{row.ends.slice(0, 5)} ({row.timezone})
                {Array.isArray(row.exams) && row.exams.length ? ` · ${(row.exams as string[]).map((e) => EXAM_NAMES[e] ?? e).join(", ")}` : ""}
              </span>
              <Button variant="ghost" disabled={remove.isPending} onClick={() => remove.mutate({ id: row.id })}>
                {t("coaching.admin.availability.remove")}
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted">{t("coaching.admin.availability.none")}</p>
      )}
      <form className="stack" onSubmit={onSubmit} noValidate aria-label={t("coaching.admin.availability.add")}>
        <div className="form-grid">
          {manager && team.length ? (
            <SelectField
              label={t("coaching.admin.availability.trainer")}
              value={form.trainer}
              options={[{ value: "", label: t("coaching.admin.availability.self") }, ...team.map((m) => ({ value: m.id, label: m.full_name }))]}
              onChange={(e) => set("trainer", e.target.value)}
            />
          ) : null}
          <SelectField
            label={t("coaching.admin.availability.weekday")}
            value={form.weekday}
            options={[0, 1, 2, 3, 4, 5, 6].map((d) => ({ value: String(d), label: t(`coaching.admin.availability.weekdays.${d}`) }))}
            onChange={(e) => set("weekday", e.target.value)}
          />
          <TextField type="date" label={t("coaching.admin.availability.date")} value={form.date} onChange={(e) => set("date", e.target.value)} />
          <TextField type="time" label={t("coaching.admin.availability.from")} value={form.starts} onChange={(e) => set("starts", e.target.value)} />
          <TextField type="time" label={t("coaching.admin.availability.to")} value={form.ends} onChange={(e) => set("ends", e.target.value)} />
          <TextField label={t("coaching.admin.availability.zone")} value={form.timezone} onChange={(e) => set("timezone", e.target.value)} />
        </div>
        <fieldset className="plain-fieldset row">
          <legend className="muted academy-small">{t("coaching.admin.availability.exams")}</legend>
          {EXAMS.map((e) => (
            <Checkbox
              key={e}
              label={EXAM_NAMES[e] ?? e}
              checked={exams.includes(e)}
              onChange={(event) => setExams((list) => (event.target.checked ? [...list, e] : list.filter((x) => x !== e)))}
            />
          ))}
        </fieldset>
        <ErrorList error={create.error ?? remove.error} />
        <div className="row">
          <Button type="submit" icon={<Plus aria-hidden size={16} />} disabled={create.isPending}>
            {t("coaching.admin.availability.add")}
          </Button>
        </div>
      </form>
    </GlassCard>
  );
}

export function OralTab() {
  const { t, i18n } = useTranslation();
  const { role } = useAcademyRoles();
  const admin = role === "owner" || role === "manager";
  const money = admin || role === "accountant";
  const [when, setWhen] = useState<"upcoming" | "past" | "">("upcoming");
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<OralBookingAdmin | null>(null);
  const close = useCallback(() => setOpen(null), []);
  const { params: examFilter } = useAdminExam();
  const list = useAcademyOralSessionsList({ page, ...(when ? { when } : {}), ...examFilter }, { query: { refetchInterval: 20_000 } });
  const data = list.data?.status === 200 ? list.data.data : undefined;
  return (
    <div className="stack">
      <StatsCard money={money} />
      <GlassCard as="section" className="stack" aria-labelledby="oral-planning-title">
        <div className="section-title">
          <h2 id="oral-planning-title">{t("coaching.admin.planning")}</h2>
          <SelectField
            label={t("coaching.admin.planning")}
            value={when}
            options={[
              { value: "upcoming", label: t("coaching.admin.upcoming") },
              { value: "past", label: t("coaching.admin.past") },
              { value: "", label: t("coaching.admin.all") },
            ]}
            onChange={(e) => {
              setWhen(e.target.value as "upcoming" | "past" | "");
              setPage(1);
            }}
          />
        </div>
        {data === undefined ? (
          <Skeleton style={{ height: 160 }} />
        ) : data.results.length ? (
          <>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t("coaching.admin.learner")}</th>
                    <th>{t("coaching.exam")}</th>
                    <th>{t("coaching.admin.trainer")}</th>
                    <th>{t("academyLive.admin.startsAt")}</th>
                    <th>{t("academyLive.admin.statusLabel")}</th>
                    {money ? <th>{t("coaching.admin.price")}</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {data.results.map((row) => {
                    const at = zonedWhen(row.starts_at, "Africa/Douala", i18n.language);
                    return (
                      <tr key={row.id}>
                        <td>
                          <button type="button" className="link-button" onClick={() => setOpen(row)}>
                            {row.learner_name || row.learner_email}
                          </button>
                        </td>
                        <td>
                          {EXAM_NAMES[row.exam] ?? row.exam}
                          {row.exam_level ? ` ${row.exam_level}` : ""}
                        </td>
                        <td>{row.trainer_full_name}</td>
                        <td className="ao-num">{t("coaching.when", at)}</td>
                        <td>
                          <Badge tone={TONES[row.status] ?? "default"}>{t(`coaching.status.${row.status}`)}</Badge>
                        </td>
                        {money ? <td className="ao-num">{row.amount_xaf != null ? formatXaf(row.amount_xaf) : "—"}</td> : null}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pager page={page} hasNext={Boolean(data.next)} onChange={setPage} />
          </>
        ) : (
          <p className="muted">{t("coaching.admin.none")}</p>
        )}
      </GlassCard>
      {role !== "accountant" ? <AvailabilityCard manager={admin} /> : null}
      {open ? <SessionSheet booking={open} onClose={close} admin={admin} /> : null}
    </div>
  );
}
